import { useMemo } from 'react';

import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { FILTER_DISPLAY_DATE_FORMAT, getOptionLabel, toIds } from '../offerFilterSchema';
import { useAuthorName, useE1ArticleTitles, useFilterSourceOptions } from '../offerFilterSources';

const formatDay = (day) => dayjs(day).format(FILTER_DISPLAY_DATE_FORMAT);

const formatDateRange = ({ after, before }) => `${after ? formatDay(after) : '…'} – ${before ? formatDay(before) : '…'}`;

/** Adı çözülemeyen kayıt (silinmiş, isteği hata vermiş) ham id'siyle gösterilir. */
const rawLabel = (id) => `#${id}`;

/**
 * Uygulanmış her alan için bir etiket: `{ field, items: [{ value, text }] }`. `text` adı yüklenirken `null`'dır.
 * Etiketler alan sırasıyla gelir. Ad istekleri yalnız etiketlerde gereken kaynaklar için atılır; istek hatası etiketi
 * düşürmez, değer ham id'siyle görünür.
 */
export const useFilterTags = (fields, values) => {
  const { formatMessage } = useIntl();

  const activeFields = useMemo(() => fields.filter((field) => field.key in values), [fields, values]);
  const usesSource = (source) => activeFields.some((field) => field.source === source);

  const committees = useFilterSourceOptions('committees', { skip: !usesSource('committees') });
  const articleGroups = useFilterSourceOptions('articleGroups', { skip: !usesSource('articleGroups') });
  const articleTypes = useFilterSourceOptions('articleTypes', { skip: !usesSource('articleTypes') });
  const author = useAuthorName(values.author);
  const e1Ids = useMemo(
    () => activeFields.filter((field) => field.source === 'e1Articles').flatMap((field) => toIds(values[field.key])),
    [activeFields, values]
  );
  const e1 = useE1ArticleTitles(e1Ids);

  const listSources = { committees, articleGroups, articleTypes };

  const resolveText = (field, value) => {
    if (field.kind === 'dateRange') {
      return formatDateRange(value);
    }
    if (field.kind === 'text') {
      return value;
    }
    if (field.kind === 'flag') {
      return 'Evet';
    }
    if (field.options) {
      const option = field.options.find((item) => String(item.value) === String(value));
      return option ? getOptionLabel(option, formatMessage) : rawLabel(value);
    }
    if (field.source === 'authors') {
      return author.isLoading ? null : author.name ?? rawLabel(value);
    }
    if (field.source === 'e1Articles') {
      return e1.isLoading ? null : e1.titles[value] ?? rawLabel(value);
    }
    const { options, isLoading } = listSources[field.source];
    return isLoading ? null : options.find((option) => option.value === value)?.label ?? rawLabel(value);
  };

  return activeFields.map((field) => {
    const value = values[field.key];
    const rawValues = field.kind === 'multi' ? value : [value];
    return { field, items: rawValues.map((item) => ({ value: item, text: resolveText(field, item) })) };
  });
};
